import { motion } from "framer-motion";
import { ArrowLeft, ShieldCheck } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";

const sections = [
  {
    title: "1. Introduction",
    content: [
      "OnTenacity (\"we\", \"our\", or \"us\") operates the ontenacity.com website and provides an AI-powered, source-backed executive intelligence platform. This Privacy Policy explains what information we collect through this website — including the AI starting-style diagnostic and demo request forms — how we use it, and the choices you have.",
      "By using this website or submitting information through our forms, you agree to the practices described in this policy.",
    ],
  },
  {
    title: "2. Information We Collect",
    content: [
      "Information you provide directly: when you complete the AI diagnostic or request a demo, we collect your name, work email address, company name, role or title, industry, phone or WhatsApp number, company website, and any message you choose to send us. The diagnostic also collects your answers to five questions about how your company invests, decides, and adopts new ways of working.",
      "Information collected automatically: like most websites, we may collect standard technical information such as your IP address, browser type, device information, pages visited, and referring URLs. We use this to operate, secure, and improve the website.",
    ],
  },
  {
    title: "3. How We Use Your Information",
    content: [
      "We use the information we collect to: generate and email your AI starting-style result; respond to demo requests and schedule walkthroughs; communicate with you about a suitable next step, where you have consented; operate, maintain, and improve our website and services; and protect the security and integrity of our systems.",
      "We do not sell your personal information, and we do not use your diagnostic answers for any purpose other than generating your result and responding to your enquiry.",
    ],
  },
  {
    title: "4. How We Share Information",
    content: [
      "We share personal information only with trusted service providers who help us operate the website and deliver communications — for example, our email delivery provider (used to send your diagnostic result and demo confirmations). These providers process information on our behalf and are bound by appropriate confidentiality and data protection obligations.",
      "We may also disclose information where required by law, regulation, or legal process, or to protect the rights, property, or safety of OnTenacity, our users, or others.",
    ],
  },
  {
    title: "5. Data Retention",
    content: [
      "We retain diagnostic submissions and demo requests for as long as needed to respond to your enquiry, maintain a record of consent, and improve our services. If you would like your information deleted, contact us at any time using the details below and we will remove it within a reasonable period, unless we are required to retain it by law.",
    ],
  },
  {
    title: "6. Data Security",
    content: [
      "We apply administrative, technical, and organizational safeguards appropriate to the sensitivity of the information we handle, including encrypted transport (HTTPS) and access controls on our systems. No method of transmission or storage is completely secure, but we work to protect your information against unauthorized access, alteration, or disclosure.",
    ],
  },
  {
    title: "7. Your Rights and Choices",
    content: [
      "Depending on your location, you may have the right to access, correct, or delete the personal information we hold about you; to withdraw consent to communications at any time; and to object to or restrict certain processing.",
      "To exercise any of these rights, email us at privacy@ontenacity.com. Every email we send in response to a diagnostic or demo request also accepts a simple reply if you wish to opt out of further contact.",
    ],
  },
  {
    title: "8. Cookies and Analytics",
    content: [
      "This website may use cookies or similar technologies for essential functionality and to understand aggregate usage patterns. You can control cookies through your browser settings; disabling them may affect how some parts of the site function.",
    ],
  },
  {
    title: "9. International Data Transfers",
    content: [
      "We may process and store information on servers located outside your country of residence. Where we do, we take steps to ensure your information receives an adequate level of protection consistent with this policy.",
    ],
  },
  {
    title: "10. Children's Privacy",
    content: [
      "This website and our services are intended for business professionals. We do not knowingly collect personal information from anyone under the age of 16. If you believe a child has provided us information, please contact us and we will delete it.",
    ],
  },
  {
    title: "11. Changes to This Policy",
    content: [
      "We may update this Privacy Policy from time to time. The \"last updated\" date at the top of this page reflects the most recent revision. Material changes will be highlighted on this page.",
    ],
  },
  {
    title: "12. Contact Us",
    content: [
      "If you have questions about this Privacy Policy or how we handle your information, contact us at privacy@ontenacity.com.",
    ],
  },
];

const PrivacyPolicy = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#050505] text-[#FAFAFA]" data-testid="privacy-policy-page">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-[#050505]/80 backdrop-blur-xl border-b border-white/10">
        <div className="max-w-4xl mx-auto px-6 py-4 flex items-center justify-between">
          <a href="/" className="text-xl font-bold font-['Outfit'] text-white" data-testid="privacy-logo">
            On<span className="text-[#00E5FF]">Tenacity</span>
          </a>
          <button
            onClick={() => navigate("/")}
            className="flex items-center gap-2 text-sm text-[#A1A1AA] hover:text-white transition-colors"
            data-testid="privacy-back-home-btn"
          >
            <ArrowLeft size={16} />
            Back to home
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-16 md:py-24">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
        >
          <span className="mono-label flex items-center gap-2">
            <ShieldCheck size={14} className="text-[#00E5FF]" />
            Legal
          </span>
          <h1 className="mt-4 text-4xl md:text-5xl font-bold tracking-tight font-['Outfit']" data-testid="privacy-title">
            Privacy Policy
          </h1>
          <p className="mt-4 text-sm text-[#71717A]">Last updated: July 2026</p>
          <p className="mt-6 text-base md:text-lg text-[#A1A1AA] leading-relaxed max-w-2xl">
            Your trust matters to us. This policy explains, in plain language, what we collect,
            why we collect it, and the control you have over it.
          </p>
        </motion.div>

        <div className="mt-12 space-y-6">
          {sections.map((section, index) => (
            <motion.section
              key={section.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.5, delay: Math.min(index * 0.03, 0.2) }}
              className="p-6 md:p-8 rounded-xl bg-[#0F0F10] border border-white/5"
              data-testid={`privacy-section-${index + 1}`}
            >
              <h2 className="text-lg md:text-xl font-semibold font-['Outfit'] text-white tracking-tight">
                {section.title}
              </h2>
              {section.content.map((paragraph, pIndex) => (
                <p key={pIndex} className="mt-4 text-sm md:text-base text-[#A1A1AA] leading-relaxed">
                  {paragraph}
                </p>
              ))}
            </motion.section>
          ))}
        </div>
      </main>

      <footer className="border-t border-white/10 py-8">
        <div className="max-w-4xl mx-auto px-6 text-center text-sm text-[#71717A]">
          © {new Date().getFullYear()} OnTenacity. All rights reserved.
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
